import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Input, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const EMPTY_FORM = { label: '', eori_no: '', is_active: true, sort_order: 0 };

const CompanyEoriNumbersSettings = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new" row
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/company-eori-numbers`, { params: { all: '1' } });
      setOptions(res.data.options || []);
    } catch (e) {
      toast.error('Failed to load company EORI numbers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (opt) => {
    setEditingId(opt.id);
    setForm({ label: opt.label, eori_no: opt.eori_no, is_active: !!opt.is_active, sort_order: opt.sort_order });
  };

  const startAdd = () => {
    setEditingId('new');
    setForm({ ...EMPTY_FORM, sort_order: options.length });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    if (!form.eori_no.trim()) { toast.error('EORI number is required'); return; }
    setSaving(true);
    try {
      const payload = { label: form.label.trim(), eori_no: form.eori_no.trim(), is_active: !!form.is_active, sort_order: parseInt(form.sort_order, 10) || 0 };
      if (editingId === 'new') {
        await axios.post(`${API_BASE_URL}/api/company-eori-numbers`, payload);
        toast.success('EORI number created');
      } else {
        await axios.put(`${API_BASE_URL}/api/company-eori-numbers/${editingId}`, payload);
        toast.success('EORI number updated');
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save EORI number');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (opt) => {
    if (!window.confirm(`Delete "${opt.label}"? Orders that already reference it keep their saved EORI number.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/company-eori-numbers/${opt.id}`);
      toast.success('EORI number deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete EORI number');
    }
  };

  const editRow = (
    <tr>
      <td><Input bsSize="sm" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. EU EORI (Netherlands)" /></td>
      <td style={{ width: 200 }}><Input bsSize="sm" value={form.eori_no} onChange={(e) => setForm((f) => ({ ...f, eori_no: e.target.value }))} placeholder="e.g. NL826581249" /></td>
      <td style={{ width: 90 }} className="text-center">
        <Input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
      </td>
      <td style={{ width: 90 }}><Input bsSize="sm" type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} /></td>
      <td style={{ width: 150 }}>
        <Button size="sm" color="primary" className="me-1" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        <Button size="sm" color="light" onClick={cancelEdit} disabled={saving}>Cancel</Button>
      </td>
    </tr>
  );

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Company EORI Numbers" parent="Settings" title="Company EORI Numbers" />
      <Container fluid={true}>
        <Row>
          <Col lg="10">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h4>Company EORI Numbers</h4>
                  <span>The EORI registrations FARA Boutique can quote on an order's documents — chosen per order when it's created.</span>
                </div>
                {editingId === null && <Button color="primary" size="sm" onClick={startAdd}>Add EORI Number</Button>}
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5"><Spinner size="sm" /> <span className="text-muted ms-1">Loading…</span></div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle">
                      <thead>
                        <tr>
                          <th>Label</th>
                          <th>EORI No.</th>
                          <th className="text-center">Active</th>
                          <th>Order</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {options.length === 0 && editingId !== 'new' && (
                          <tr><td colSpan="5" className="text-center text-muted py-3">No EORI numbers configured yet.</td></tr>
                        )}
                        {options.map((opt) => (
                          editingId === opt.id ? (
                            <React.Fragment key={opt.id}>{editRow}</React.Fragment>
                          ) : (
                            <tr key={opt.id}>
                              <td>{opt.label}</td>
                              <td>{opt.eori_no}</td>
                              <td className="text-center">{opt.is_active ? 'Yes' : 'No'}</td>
                              <td>{opt.sort_order}</td>
                              <td>
                                <Button size="sm" color="light" className="me-1" onClick={() => startEdit(opt)} disabled={editingId !== null}>Edit</Button>
                                <Button size="sm" color="danger" outline onClick={() => remove(opt)} disabled={editingId !== null}>Delete</Button>
                              </td>
                            </tr>
                          )
                        ))}
                        {editingId === 'new' && editRow}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CompanyEoriNumbersSettings;
